'use client'
import type { CartItemType } from '../../lib/utils/cart'
import type { CheckoutPaymentMethod } from '../../lib/utils/payment-methods'
import { useTranslations } from 'next-intl'

import { useEffect, useState } from 'react'
import { useRouter } from '../../i18n/routing'
import { trackEvent } from '../../lib/analytics/client'
import { getCartSummaryAnalyticsProperties } from '../../lib/analytics/events'
import useGetCart from '../../lib/hooks/cart/use-get-cart'
import {
  getCardCheckoutPaymentMethod,
  getDefaultCheckoutPaymentMethod,
  resolveCheckoutPaymentMethods,
} from '../../lib/utils/payment-methods'
import { formatPrecioESCustom } from '../../lib/utils/price-formatter'
import { CardIcon } from '../common/icons/card'
import { PaymentMethodIcon } from '../common/icons/payment-method'
import { TruckIcon } from '../common/icons/truck'
import { normalizeShippingCountry } from './common/country-utils'
import { PayButton } from './components/pay-button'
import { PriceSummary } from './price-summary'

export type OrderSummaryState = 'shopping' | 'address' | 'payment'

interface OrderSummaryProps {
  cartItems: CartItemType[]
  route: string
  state: OrderSummaryState
  buttonText?: string
  isHydratingPayment?: boolean
  location?: string
  country?: string
  island?: string
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  cartItems,
  route,
  state,
  buttonText,
  isHydratingPayment = false,
  location,
  country,
  island,
}) => {
  const t = useTranslations('common.cart')
  const visaIcon = '/images/visa.png'
  const amexIcon = '/images/american-express.png'
  const router = useRouter()

  const normalizedCountry = normalizeShippingCountry(country)
  const { data: cartData } = useGetCart({
    location,
    country: normalizedCountry,
    island,
  })
  const backendSummary = cartData?.summary
  const cartSummaryAnalyticsProperties = getCartSummaryAnalyticsProperties({
    cartItems,
    summary: backendSummary,
  })

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => {
      const itemTotal = parseFloat(
        item.totalPrice?.replace('€', '').replace(',', '.') || '0',
      )
      return total + itemTotal
    }, 0)
  }

  const subtotal = backendSummary?.subtotal ?? calculateSubtotal()
  const vatRate = backendSummary?.vat_rate ?? 0.21
  const calculatedVatAmount = subtotal * vatRate
  const calculatedTotalWithVATNoShipping = subtotal + calculatedVatAmount
  const totalWithVATNoShipping
    = backendSummary?.total_with_vat_no_shipping
      ?? calculatedTotalWithVATNoShipping

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) {
      return '-'
    }
    return formatPrecioESCustom(amount)
  }

  const availablePaymentMethods = resolveCheckoutPaymentMethods(
    cartData?.available_payment_methods,
    cartData?.shipping_error,
  )
  const availablePaymentMethodsKey = availablePaymentMethods.join('|')
  const cardPaymentMethod = getCardCheckoutPaymentMethod(availablePaymentMethods)
  const isManualOnly = availablePaymentMethods[0] === 'manual'
  const isFullCardPayment = cardPaymentMethod === 'redsys_100'
  const cardPaymentInputId = 'payment-method-card'
  const transferPaymentInputId = 'payment-method-transfer'

  const [paymentMethod, setPaymentMethod] = useState(
    getDefaultCheckoutPaymentMethod(availablePaymentMethods),
  )

  const handleConfirmOrderClick = () => {
    trackEvent('car_list_confirmar_pedido', cartSummaryAnalyticsProperties)
    router.push(route)
  }

  const handlePaymentMethodChange = (method: CheckoutPaymentMethod) => {
    setPaymentMethod(method)

    if (method === 'manual') {
      return
    }

    if (method === 'transfer') {
      trackEvent('car_res_pago_transf', {
        ...cartSummaryAnalyticsProperties,
        payment_method: 'transfer',
      })
      return
    }

    trackEvent('car_res_pago_tarjeta', {
      ...cartSummaryAnalyticsProperties,
      payment_method: 'card',
    })
  }

  useEffect(() => {
    setPaymentMethod(currentPaymentMethod => (
      availablePaymentMethods.includes(currentPaymentMethod)
        ? currentPaymentMethod
        : getDefaultCheckoutPaymentMethod(availablePaymentMethods)
    ))
  }, [availablePaymentMethods, availablePaymentMethodsKey])

  const defaultButtonText = {
    shopping: t('calculateShippingAndConfirmOrder', {
      defaultValue: 'Calcular portes y confirmar pedido',
    }),
    address: null,
    payment: t('confirmOrder', { defaultValue: 'Confirmar pedido' }),
  }

  const finalButtonText = buttonText || defaultButtonText[state]

  return (
    <aside className="flex-1 order-[summary]">
      {state !== 'payment' && (
        <>
          <h2 className="mb-[18px] text-[21px] font-bold">
            {t('orderSummary', { defaultValue: 'Resumen del pedido' })}
          </h2>

          <div className="mb-0">
            {cartItems.map((item, index) => (
              <div key={index} className="flex justify-between items-start gap-3">
                <div className="pr-[12px] flex-1 min-w-0">
                  <div className="flex items-baseline gap-[8px] min-w-0">
                    <p className="line-clamp-1 text-[17px] shrink min-w-0">
                      -
                      {' '}
                      {item.name}
                    </p>
                    <span className="text-[17px] whitespace-nowrap shrink-0">
                      x
                      {item.quantity}
                      {item.unit === 'm2' || item.unit === 'm²'
                        ? ' m²'
                        : item.unit === 'ud' || item.unit === 'unidad'
                          ? ' ud'
                          : ''}
                    </span>
                  </div>
                  {item.availability ? (
                    <p className="mt-1 text-[13px] text-stone-500">
                      {t('productionTimeLabel', {
                        defaultValue: 'Tiempo de fabricación',
                      })}
                      :
                      {' '}
                      {item.availability}
                    </p>
                  ) : null}
                </div>
                <p className="text-[17px] min-w-[100px] text-right shrink-0">
                  {item.totalPrice}
                </p>
              </div>
            ))}
          </div>
        </>
      )}

      {state === 'address' ? (
        <PriceSummary
          location={location}
          country={normalizedCountry}
          island={island}
        />
      ) : state === 'payment' ? (
        <>
          <h2 className="mb-5 text-[21px] font-bold">
            {t('paymentMethod', { defaultValue: 'Forma de pago' })}
          </h2>

          {isHydratingPayment ? (
            <div className="space-y-5 mb-4">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex size-8 items-center justify-center rounded-full border border-stone-300 bg-white">
                    <div className="size-4 rounded-full bg-stone-200 animate-pulse"></div>
                  </div>
                  <div className="h-8 w-52 rounded bg-stone-200 animate-pulse"></div>
                </div>
                <div className="ml-11 space-y-3">
                  <div className="h-5 w-full rounded bg-stone-100 animate-pulse"></div>
                  <div className="h-5 w-5/6 rounded bg-stone-100 animate-pulse"></div>
                  <div className="h-5 w-3/4 rounded bg-stone-100 animate-pulse"></div>
                </div>
              </div>
              <div className="h-16 w-full rounded-xl bg-amber-100 animate-pulse"></div>
              <div className="flex gap-2">
                <div className="h-8 w-14 rounded bg-stone-100 animate-pulse"></div>
                <div className="h-8 w-14 rounded bg-stone-100 animate-pulse"></div>
                <div className="h-8 w-14 rounded bg-stone-100 animate-pulse"></div>
                <div className="h-8 w-14 rounded bg-stone-100 animate-pulse"></div>
              </div>
            </div>
          ) : isManualOnly ? (
            <div className="mb-8">
              <PayButton
                cartAnalytics={cartSummaryAnalyticsProperties}
                paymentMethod="manual"
                disabled={cartItems.length === 0}
              />
            </div>
          ) : (
            <>
              {cardPaymentMethod && (
                <div className="mb-5">
                  <label
                    htmlFor={cardPaymentInputId}
                    className="flex items-center gap-2 mb-2 cursor-pointer"
                  >
                    <input
                      id={cardPaymentInputId}
                      type="radio"
                      name="payment-method"
                      value={cardPaymentMethod}
                      checked={paymentMethod === cardPaymentMethod}
                      onChange={() => handlePaymentMethodChange(cardPaymentMethod)}
                      className="sr-only"
                    />
                    <span
                      aria-hidden="true"
                      className="size-5 rounded-full border border-black flex items-center justify-center"
                    >
                      {paymentMethod === cardPaymentMethod && (
                        <div className="size-3 rounded-full bg-black"></div>
                      )}
                    </span>
                    <span className="text-lg font-bold">
                      {t('cardPayment', { defaultValue: 'Pago con tarjeta' })}
                    </span>
                  </label>
                  <p className="text-sm ml-7">
                    {isFullCardPayment
                      ? t('cardPaymentDescriptionFull', {
                          defaultValue:
                            'Paga con tarjeta utilizando los servicios de Redsys. Pagarás el 100% del importe total',
                        })
                      : t('cardPaymentDescription', {
                          defaultValue:
                            'Paga con tarjeta utilizando los servicios de Redsys. Pagarás el 50% del importe total',
                        })}
                    {' '}
                    {t('cardPaymentDetails', {
                      defaultValue:
                        'y empezaremos a gestionar tu pedido inmediatamente tras el pago.',
                    })}
                  </p>
                </div>
              )}

              {availablePaymentMethods.includes('transfer') && (
                <div className="mb-8">
                  <label
                    htmlFor={transferPaymentInputId}
                    className="flex items-center gap-2 mb-2 cursor-pointer"
                  >
                    <input
                      id={transferPaymentInputId}
                      type="radio"
                      name="payment-method"
                      value="transfer"
                      checked={paymentMethod === 'transfer'}
                      onChange={() => handlePaymentMethodChange('transfer')}
                      className="sr-only"
                    />
                    <span
                      aria-hidden="true"
                      className="size-5 rounded-full border border-black flex items-center justify-center"
                    >
                      {paymentMethod === 'transfer' && (
                        <div className="size-3 rounded-full bg-black"></div>
                      )}
                    </span>
                    <span className="text-lg font-bold">
                      {t('bankTransfer', {
                        defaultValue: 'Transferencia bancaria',
                      })}
                    </span>
                  </label>
                  <p className="text-sm ml-7">
                    <span className="font-bold">
                      {t('bankTransferDescription', {
                        defaultValue:
                          'Realiza el pago del 50% del total en nuestra cuenta bancaria.',
                      })}
                      {' '}
                    </span>
                    {t('bankTransferDetails', {
                      defaultValue:
                        'Recibirás la información del pago por mail tras solicitar el pedido. Tu pedido no se procesará hasta que se haya recibido el importe. Podrás abonar el 50% restante cuando el material esté listo.',
                    })}
                  </p>
                </div>
              )}

              <PayButton
                cartAnalytics={cartSummaryAnalyticsProperties}
                paymentMethod={paymentMethod}
                disabled={cartItems.length === 0}
              />

              <div className="flex gap-2 mb-4">
                <img
                  src={visaIcon}
                  alt="Visa"
                  className="h-6 w-[35px] rounded-[2px] border-[#d6d6d6] object-contain border"
                />
                <img
                  src="/images/mastercard.png"
                  alt="Mastercard"
                  className="h-6 w-[35px] border-[#d6d6d6] rounded-[2px] object-contain border py-[3px]"
                />
                <img
                  src={amexIcon}
                  alt="American Express"
                  className="h-6 w-[35px] border-[#d6d6d6] rounded-[2px] object-contain border py-[3px]"
                />
                <PaymentMethodIcon className="h-6" />
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="flex justify-between items-center px-0 py-5 mx-0 my-5 border-y border-solid border-y-black">
            <p className="text-xl font-bold">
              {t('total', { defaultValue: 'Total' })}
              {' '}
              <span className="text-[12px]">
                (
                {t('withVAT', { defaultValue: 'con iva' })}
                )
              </span>
            </p>
            <p className="text-xl font-bold">
              {formatCurrency(totalWithVATNoShipping)}
            </p>
          </div>
        </>
      )}

      {state === 'shopping' && (
        <>
          <button
            className="px-[33.5px] py-2.5 font-medium w-full text-xl bg-amber-300 rounded-xl cursor-pointer border-[none] mb-5"
            onClick={handleConfirmOrderClick}
          >
            {finalButtonText}
          </button>
          <div className="flex gap-2 mb-4">
            <img
              src={visaIcon}
              alt="Visa"
              className="h-6 w-[35px] rounded-[2px] border-[#d6d6d6] object-contain border"
            />
            <img
              src="/images/mastercard.png"
              alt="Mastercard"
              className="h-6 w-[35px] border-[#d6d6d6] rounded-[2px] object-contain border py-[3px]"
            />
            <img
              src={amexIcon}
              alt="American Express"
              className="h-6 w-[35px] border-[#d6d6d6] rounded-[2px] object-contain border py-[3px]"
            />
            <PaymentMethodIcon className="h-6" />
          </div>
        </>
      )}

      {state !== 'payment' && (
        <div className="flex gap-4 p-4 mb-4 rounded-2xl border border-black border-solid">
          <TruckIcon className="min-w-[22px] min-h-[15px] flex-1" />
          <p className="text-xs whitespace-pre-line">
            {t('curbsideDelivery', {
              defaultValue: 'Entrega a pie de calle con camión plataforma.',
            })}
            {'\n'}
            <span className="font-bold">
              {t('accessibleLocation', {
                defaultValue:
                  'Es imprescindible que la ubicación sea accesible y esté asfaltada.',
              })}
              {' '}
            </span>
            {t('sustainablePackaging', {
              defaultValue:
                'Este importe incluye también el embalaje reutilizable de madera, eliminando todos los plásticos de un solo uso.',
            })}
          </p>
        </div>
      )}

      <div className="flex gap-4 p-4 rounded-2xl border border-black border-solid">
        <CardIcon className="min-w-[22px] min-h-[15px] flex-0" />
        <p className="text-xs whitespace-pre-line">
          {t('securePayment')}
          {'\n'}
          <span className="font-bold">{t('moreAboutPaymentMethods')}</span>
        </p>
      </div>
    </aside>
  )
}
